// Generic client-side filter for review / list pages: free text over every
// human-readable string in a row (names, titles, partner / company names,
// responsible people, emails, statuses - ids, tokens and urls excluded),
// plus a day filter over the row's date fields (due / submitted / created /
// updated / completed / meeting ...). "Today" is the day filter set to today.
//
//   matchesReviewFilter(row, { q: "bes", date: "2026-08-19" })
import { localDayKey, utcDayKey, inDayRange, rangeEmpty } from "helpers/dateRange";

const SKIP_KEY = /(^_id$|Id$|Ids$|^id$|token|url|hash|^__v$|password|color)/i;
const DATE_KEY = /(date|At$|^at$|submitted|created|updated|completed|resolved|due|meeting|raised|closed|approved|timestamp)/i;

function collect(v, depth, strings, dates, key) {
  if (v === null || v === undefined || depth > 3) return;
  if (v instanceof Date) { dates.push({ key, d: v }); return; }
  if (typeof v === "string") {
    if (key && DATE_KEY.test(key) && /^\d{4}-\d{2}-\d{2}/.test(v)) { const d = new Date(v); if (!isNaN(+d)) dates.push({ key, d }); }
    if (v.length <= 300) strings.push(v);
    return;
  }
  if (typeof v === "number" || typeof v === "boolean") { if (key && !SKIP_KEY.test(key)) strings.push(String(v)); return; }
  if (Array.isArray(v)) { for (const x of v.slice(0, 50)) collect(x, depth + 1, strings, dates, key); return; }
  if (typeof v === "object") {
    for (const k of Object.keys(v)) {
      if (SKIP_KEY.test(k) && !DATE_KEY.test(k)) continue;
      collect(v[k], depth + 1, strings, dates, k);
    }
  }
}

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export function rowSearchText(row) {
  const strings = [], dates = [];
  collect(row, 0, strings, dates, "");
  return strings.join(" ").toLowerCase();
}

export function rowDates(row) {
  const strings = [], dates = [];
  collect(row, 0, strings, dates, "");
  return dates.map((x) => x.d);
}

// ─── Start / due ranges ───────────────────────────────────────────────────
//
// The day filter above asks "anything dated this day", which is the right
// question for a reviewer scanning one morning's work and the wrong one for
// a window. A range has to know WHICH date it is ranging over, and the eight
// tabs here do not share a field name: a revision has `ts`, a proposal
// `createdAt`, a checklist completion `completedAt` and `dueDate`, an
// imported plan row `startDate` and `endDate`.
//
// So each side is a priority list of field names rather than one field, and
// the first one a row actually carries wins. A row with none is out of the
// window - the same rule the other three pages use for an undated row.
//
// `utc` marks the DATE-ONLY fields (stored midnight UTC, compared as the
// calendar day they name). The rest are instants, read in the reader's own
// day. Mixing those two up moves due dates a day for everyone off UTC -
// see helpers/dateRange.js.
const START_FIELDS = [
  { key: "createdat" }, { key: "ts" }, { key: "submittedat" },
  { key: "startdate", utc: true }, { key: "completedat" }, { key: "openedat" },
];
const END_FIELDS = [
  { key: "duedate", utc: true }, { key: "enddate", utc: true },
  { key: "plannedend", utc: true }, { key: "meetingdate", utc: true },
];

function dayKeyFor(row, fields) {
  const strings = [], dates = [];
  collect(row, 0, strings, dates, "");
  if (!dates.length) return "";
  for (const f of fields) {
    const hit = dates.find((x) => String(x.key || "").toLowerCase() === f.key);
    if (hit) return f.utc ? utcDayKey(hit.d) : localDayKey(hit.d);
  }
  return "";
}

export function matchesReviewFilter(row, filter) {
  if (!filter) return true;
  const q = String(filter.q || "").trim().toLowerCase();
  if (q && !rowSearchText(row).includes(q)) return false;
  if (filter.date) {
    const day = new Date(filter.date);
    if (!isNaN(+day) && !rowDates(row).some((d) => sameDay(d, day))) return false;
  }
  if (!rangeEmpty(filter.startRange)) {
    const k = dayKeyFor(row, START_FIELDS);
    if (!inDayRange(k, filter.startRange.from, filter.startRange.to)) return false;
  }
  if (!rangeEmpty(filter.dueRange)) {
    const k = dayKeyFor(row, END_FIELDS);
    if (!inDayRange(k, filter.dueRange.from, filter.dueRange.to)) return false;
  }
  return true;
}

export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
